import React, { useMemo, useState } from "react";
import axios from "axios";
import { Badge, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import {
  FiCheckCircle,
  FiClock,
  FiCopy,
  FiDownload,
  FiExternalLink,
  FiFileText,
  FiGlobe,
  FiHash,
  FiRefreshCw,
  FiSearch,
  FiXCircle,
} from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";

const API_BASE_URL = (process.env.REACT_APP_API_URL || "https://tools.microters.com/").replace(
  /\/$/,
  ""
);

const MAX_URLS = 20;

const parseTextareaUrls = (value) =>
  [...new Set(String(value || "").split(/\r?\n/).map((url) => url.trim()).filter(Boolean))];

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  return `${(value / 1024).toFixed(1)} KB`;
};

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const ULKWebWordCounter = () => {
  const [urlsText, setUrlsText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const enteredUrls = useMemo(() => parseTextareaUrls(urlsText), [urlsText]);

  const handleCheck = async (event) => {
    event.preventDefault();

    if (enteredUrls.length === 0) {
      const message = "Please enter at least one URL, one URL per line.";
      setError(message);
      toast.error(message);
      return;
    }

    if (enteredUrls.length > MAX_URLS) {
      const message = `You can check a maximum of ${MAX_URLS} unique URLs at once.`;
      setError(message);
      toast.error(message);
      return;
    }

    setLoading(true);
    setError("");
    setData(null);

    console.log("[WEB WORD COUNTER UI] Sending URLs:", enteredUrls);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/web-word-counter`,
        { urls: enteredUrls },
        { timeout: 90000 }
      );

      if (!response.data?.success || !response.data?.data) {
        throw new Error(response.data?.message || "The server returned an invalid response.");
      }

      console.log("[WEB WORD COUNTER UI] Results received:", response.data.data);
      setData(response.data.data);

      if (response.data.data.summary.failedUrls > 0) {
        toast.warning(
          `Completed with ${response.data.data.summary.failedUrls} failed URL(s).`
        );
      } else {
        toast.success("All URLs were counted successfully.");
      }
    } catch (requestError) {
      const message =
        requestError.response?.data?.message ||
        requestError.message ||
        "Unable to count words for these URLs.";

      console.error("[WEB WORD COUNTER UI] Request failed:", {
        message,
        status: requestError.response?.status,
        data: requestError.response?.data,
      });

      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setUrlsText("");
    setData(null);
    setError("");
  };

  const downloadCsv = () => {
    if (!data?.results?.length) return;

    const headers = [
      "Requested URL",
      "Final URL",
      "Status",
      "Title",
      "Words",
      "Unique Words",
      "Reading Time (minutes)",
      "HTML Size",
      "Top Keywords",
      "Error",
    ];

    const rows = data.results.map((item) => [
      item.requestedUrl,
      item.finalUrl || "",
      item.success ? "Success" : "Failed",
      item.title || "",
      item.wordCount || 0,
      item.uniqueWordCount || 0,
      item.readingTimeMinutes || 0,
      item.bytes || 0,
      item.topKeywords?.map((keyword) => `${keyword.word} (${keyword.count})`).join(", ") || "",
      item.error || "",
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\r\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = downloadUrl;
    link.download = "web-word-count-results.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(downloadUrl);
    toast.success("CSV downloaded.");
  };

  const copySummary = async () => {
    if (!data?.results?.length) return;

    const text = data.results
      .map((item) =>
        item.success
          ? `${item.finalUrl} — ${item.wordCount} words, ${item.uniqueWordCount} unique words`
          : `${item.requestedUrl} — Failed: ${item.error}`
      )
      .join("\n");

    try {
      await navigator.clipboard.writeText(text);
      toast.success("Results copied.");
    } catch (copyError) {
      console.error("[WEB WORD COUNTER UI] Copy failed:", copyError);
      toast.error("Could not copy the results.");
    }
  };

  return (
    <div className="min-vh-100 bg-light py-5">
      <ToastContainer position="top-right" autoClose={2500} />

      <Container>
        <header className="text-center mb-5">
          <h1 className="display-3 fw-bold text-dark lh-sm mb-3">
            ULK Web Word <span className="text-danger">Counter Tool</span>
          </h1>
          <p className="lead text-secondary mx-auto mb-0">
            The ULK Web Word Counter Tool is a free and easy-to-use tool for keyword clustering.
            It simplifies keyword research and grouping to help users improve their SEO strategies
            and content planning.
          </p>
        </header>

        <Card className="border shadow-sm rounded-4">
          <Card.Body className="p-4">
            <Form onSubmit={handleCheck} noValidate>
              <Form.Group controlId="ulkUrlsList" className="mb-3">
                <Form.Label className="fw-bold small mb-2">
                  <FiSearch className="text-danger me-1" aria-hidden="true" />
                  URLs List <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={urlsText}
                  onChange={(event) => {
                    setUrlsText(event.target.value);
                    if (error) setError("");
                  }}
                  placeholder={"Enter URLs (one per line)\nhttps://example.com\nhttps://mysite.com/blog"}
                  disabled={loading}
                  spellCheck="false"
                  isInvalid={Boolean(error)}
                />
                <Form.Text className="d-block text-end">
                  {enteredUrls.length}/{MAX_URLS} URLs
                </Form.Text>
                {error && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {error}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Button
                variant="danger"
                type="submit"
                disabled={loading}
                className="w-100 rounded-pill fw-bold py-3 d-flex align-items-center justify-content-center gap-2"
              >
                {loading ? (
                  <Spinner animation="border" size="sm" aria-hidden="true" />
                ) : (
                  <FiSearch aria-hidden="true" />
                )}
                {loading ? "Checking web pages..." : "Perform Check"}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {data && (
          <Card className="border shadow-sm rounded-4 mt-4">
            <Card.Header className="bg-white p-4 border-bottom">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                <div>
                  <small className="text-danger fw-bold text-uppercase">
                    Live analysis complete
                  </small>
                  <h2 className="h4 fw-bold text-dark mb-0 mt-1">Word Count Results</h2>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={copySummary}>
                    <FiCopy className="me-1" /> Copy
                  </Button>
                  <Button variant="danger" size="sm" onClick={downloadCsv}>
                    <FiDownload className="me-1" /> CSV
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={handleReset}>
                    <FiRefreshCw className="me-1" /> Reset
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-4">
              <Row xs={2} md={4} className="g-3 mb-4">
                <Col>
                  <Card className="h-100 border-0 bg-light">
                    <Card.Body className="d-flex align-items-center gap-3 p-3">
                      <FiGlobe className="text-success fs-4 flex-shrink-0" />
                      <div>
                        <small className="text-secondary text-uppercase">Successful URLs</small>
                        <div className="h5 fw-bold mb-0">
                          {formatNumber(data.summary.successfulUrls)}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col>
                  <Card className="h-100 border-0 bg-light">
                    <Card.Body className="d-flex align-items-center gap-3 p-3">
                      <FiFileText className="text-success fs-4 flex-shrink-0" />
                      <div>
                        <small className="text-secondary text-uppercase">Total Words</small>
                        <div className="h5 fw-bold mb-0">
                          {formatNumber(data.summary.totalWords)}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col>
                  <Card className="h-100 border-0 bg-light">
                    <Card.Body className="d-flex align-items-center gap-3 p-3">
                      <FiHash className="text-success fs-4 flex-shrink-0" />
                      <div>
                        <small className="text-secondary text-uppercase">Average Words</small>
                        <div className="h5 fw-bold mb-0">
                          {formatNumber(data.summary.averageWords)}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col>
                  <Card className="h-100 border-0 bg-light">
                    <Card.Body className="d-flex align-items-center gap-3 p-3">
                      {data.summary.failedUrls ? (
                        <FiXCircle className="text-danger fs-4 flex-shrink-0" />
                      ) : (
                        <FiCheckCircle className="text-success fs-4 flex-shrink-0" />
                      )}
                      <div>
                        <small className="text-secondary text-uppercase">Failed URLs</small>
                        <div className="h5 fw-bold mb-0">
                          {formatNumber(data.summary.failedUrls)}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <div className="d-grid gap-3">
                {data.results.map((item, index) => (
                  <Card
                    key={`${item.requestedUrl}-${index}`}
                    className={item.success ? "border" : "border-danger"}
                  >
                    <Card.Body className="p-3 p-md-4">
                      <div className="d-flex align-items-start gap-3">
                        <Badge bg="danger" pill className="flex-shrink-0 py-2 px-3">
                          {index + 1}
                        </Badge>

                        <div className="flex-grow-1 overflow-hidden">
                          <Badge bg={item.success ? "success" : "danger"} className="mb-2">
                            {item.success ? (
                              <FiCheckCircle className="me-1" />
                            ) : (
                              <FiXCircle className="me-1" />
                            )}
                            {item.success ? "Counted" : "Failed"}
                          </Badge>

                          <h3 className="h6 fw-bold text-dark mb-1 text-break">
                            {item.success ? item.title : item.requestedUrl}
                          </h3>

                          {item.success && (
                            <a
                              href={item.finalUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="small text-secondary text-decoration-none text-break"
                            >
                              {item.finalUrl} <FiExternalLink />
                            </a>
                          )}
                        </div>
                      </div>

                      {item.success ? (
                        <>
                          <Row xs={2} md={4} className="g-2 mt-2">
                            <Col>
                              <div className="bg-light rounded p-2 h-100">
                                <small className="text-secondary text-uppercase d-block">Words</small>
                                <strong>{formatNumber(item.wordCount)}</strong>
                              </div>
                            </Col>
                            <Col>
                              <div className="bg-light rounded p-2 h-100">
                                <small className="text-secondary text-uppercase d-block">Unique</small>
                                <strong>{formatNumber(item.uniqueWordCount)}</strong>
                              </div>
                            </Col>
                            <Col>
                              <div className="bg-light rounded p-2 h-100">
                                <small className="text-secondary text-uppercase d-block">
                                  Reading time
                                </small>
                                <strong>
                                  <FiClock className="me-1" /> {item.readingTimeMinutes} min
                                </strong>
                              </div>
                            </Col>
                            <Col>
                              <div className="bg-light rounded p-2 h-100">
                                <small className="text-secondary text-uppercase d-block">
                                  HTML size
                                </small>
                                <strong>{formatBytes(item.bytes)}</strong>
                              </div>
                            </Col>
                          </Row>

                          {item.topKeywords?.length > 0 && (
                            <div className="mt-3">
                              <small className="text-secondary text-uppercase fw-bold d-block mb-2">
                                Top keywords
                              </small>
                              <div className="d-flex flex-wrap gap-2">
                                {item.topKeywords.map((keyword) => (
                                  <Badge
                                    bg="light"
                                    text="dark"
                                    className="border fw-normal py-2 px-3"
                                    pill
                                    key={keyword.word}
                                  >
                                    {keyword.word}{" "}
                                    <span className="text-danger fw-bold">{keyword.count}</span>
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="alert alert-danger small mb-0 mt-3" role="alert">
                          {item.error}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default ULKWebWordCounter;
